import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilterButton,
  FilterContainer,
  FilterCount,
  FilterRow,
  MediaTypeButton,
  MediaTypeContainer,
  PageContainer,
  UploadMediaButton,
} from './UploadedMediaPage.styles';
import { useNavigate, useParams } from 'react-router-dom';
import { VAULT, VAULT_MEDIA } from '../../../components/App/routes';
import { ALBUM_MEDIA_TYPE, CRM_ORGANIZATION_MEMBER_ROLE, MEDIA_ASSET_SALE_TAG } from '../../../utils/constants';
import {
  AddMediaToFolderModal,
  AlbumMediaList,
  EditAlbumMediaModal,
  SelectedItemsOptions,
  UploadAlbumMediaModal,
} from '../../../components';
import { ReactComponent as DollarIcon } from '../../../assets/icons/dollar.svg';
import { ReactComponent as BoxIcon } from '../../../assets/icons/box.svg';
import { ReactComponent as FolderIcon } from '../../../assets/icons/folder.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAlbumMedia } from '../../../store/slices/user/asyncThunks';
import PreviewAlbumMediaModal from '../../../components/modals/PreviewAlbumMediaModal/PreviewAlbumMediaModal';
import { addMediaToAssetFolder } from '../../../store/slices/assetFolder/asyncThunks';
import { notifyError, notifyInfo } from '../../../utils/notify';
import { getAlbumMediaTags } from '../../../store/slices/influencerRequest/asyncThunks';
import { selectFetchAlbumMediaInfo, selectUser } from '../../../store/slices/user/slice';

const MEDIA_TYPE = {
  FREE: 'free',
  PAID: 'paid',
  BUNDLE_ONLY: 'bundle-only',
};

const FILTERS = [
  { title: 'All', value: null, countKey: 'all' },
  { title: 'Photos', value: ALBUM_MEDIA_TYPE.PHOTO, countKey: 'photo' },
  { title: 'Videos', value: ALBUM_MEDIA_TYPE.VIDEO, countKey: 'video' },
];

const UploadedMediaPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type, influencerId } = useParams();
  const [selectedFilter, setSelectedFilter] = useState(FILTERS[0]);
  const [page, setPage] = useState(1);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const modalRef = useRef();
  const previewAlbumMediaModalRef = useRef();
  const addMediaToFolderModalRef = useRef();
  const editAlbumMediaModalRef = useRef();
  const user = useSelector(selectUser);
  const { counts } = useSelector(selectFetchAlbumMediaInfo);

  useEffect(() => {
    dispatch(getAlbumMediaTags());
  }, [dispatch]);

  const tag = useMemo(() => {
    switch (type) {
      case MEDIA_TYPE.FREE:
        return [MEDIA_ASSET_SALE_TAG.SINGLE_FREE];
      case MEDIA_TYPE.PAID:
        return [MEDIA_ASSET_SALE_TAG.SINGLE_PAID];
      case MEDIA_TYPE.BUNDLE_ONLY:
        return [MEDIA_ASSET_SALE_TAG.BUNDLE];
      default:
        break;
    }
  }, [type]);

  const modalTag = useMemo(
    () => (type === MEDIA_TYPE.FREE ? MEDIA_ASSET_SALE_TAG.SINGLE_FREE : MEDIA_ASSET_SALE_TAG.SINGLE_PAID),
    [type],
  );

  const onMediaClick = useCallback(media => {
    setSelectedMedia(media);
    previewAlbumMediaModalRef.current.show();
  }, []);

  const onSelectItem = useCallback(media => {
    setSelectedItems(prev => {
      if (prev.find(m => m.id === media.id)) {
        return prev.filter(m => m.id !== media.id);
      } else {
        return [...prev, media];
      }
    });
  }, []);

  const onEditClick = useCallback(media => {
    setSelectedMedia(media);
    editAlbumMediaModalRef.current.show();
  }, []);

  const onAddMediaToFolder = folder => {
    dispatch(
      addMediaToAssetFolder({
        folderId: folder.id,
        media: selectedItems.map(item => ({ mediaId: item.id, type: item.kind })),
        ...(influencerId && { influencerId }),
      }),
    )
      .unwrap()
      .then(() => {
        setSelectedItems([]);
        addMediaToFolderModalRef.current.hide();
        notifyInfo('Media added to folder successfully!');
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <>
      <PageContainer>
        <MediaTypeContainer>
          <MediaTypeButton
            onClick={() => {
              navigate(
                `${influencerId ? `/${influencerId}` : ''}/${VAULT}/${VAULT_MEDIA.replace(':type', MEDIA_TYPE.FREE)}`,
              );
            }}
            $selected={type === MEDIA_TYPE.FREE}>
            <BoxIcon />
            Free
          </MediaTypeButton>
          <MediaTypeButton
            onClick={() => {
              navigate(
                `${influencerId ? `/${influencerId}` : ''}/${VAULT}/${VAULT_MEDIA.replace(':type', MEDIA_TYPE.PAID)}`,
              );
            }}
            $selected={type === MEDIA_TYPE.PAID}>
            <DollarIcon />
            Paid
          </MediaTypeButton>
          <MediaTypeButton
            onClick={() => {
              navigate(
                `${influencerId ? `/${influencerId}` : ''}/${VAULT}/${VAULT_MEDIA.replace(
                  ':type',
                  MEDIA_TYPE.BUNDLE_ONLY,
                )}`,
              );
            }}
            $selected={type === MEDIA_TYPE.BUNDLE_ONLY}>
            <FolderIcon />
            Bundle Only
          </MediaTypeButton>
        </MediaTypeContainer>
        <FilterContainer>
          <FilterRow>
            {FILTERS.map(f => {
              const isSelected = f.value === selectedFilter.value;
              const count = counts?.[f.countKey];
              return (
                <FilterButton $selected={isSelected} onClick={() => setSelectedFilter(f)} key={f.value}>
                  {f.title}
                  {typeof count === 'number' && <FilterCount $selected={isSelected}>{count}</FilterCount>}
                </FilterButton>
              );
            })}
          </FilterRow>
          {[
            CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
            CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
            CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
            CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
          ].includes(user.role) && (
            <>
              {type !== MEDIA_TYPE.BUNDLE_ONLY && (
                <UploadMediaButton onClick={() => modalRef.current.show()}>
                  Upload {type === MEDIA_TYPE.PAID ? 'Paid' : 'Free'}
                </UploadMediaButton>
              )}
            </>
          )}
        </FilterContainer>
        <AlbumMediaList
          kind={selectedFilter.value}
          tag={tag}
          page={page}
          setPage={setPage}
          previewAlbumMediaModalRef={previewAlbumMediaModalRef}
          onMediaClick={onMediaClick}
          exactMatch={type !== MEDIA_TYPE.PAID}
          selectedItems={selectedItems}
          onSelectClick={onSelectItem}
          onEditClick={onEditClick}
          influencerId={influencerId}
        />
        {!!selectedItems.length && (
          <SelectedItemsOptions
            selectedItems={selectedItems}
            onUnselectClick={() => setSelectedItems([])}
            onBtnClick={() => {
              addMediaToFolderModalRef.current.show();
            }}
          />
        )}
      </PageContainer>
      <UploadAlbumMediaModal
        modalRef={modalRef}
        tag={modalTag}
        influencerId={influencerId}
        onSuccess={() => {
          setPage(1);
          dispatch(
            fetchAlbumMedia({ page: 1, kind: selectedFilter.value, tag, ...(influencerId && { influencerId }) }),
          );
        }}
      />
      <PreviewAlbumMediaModal
        modalRef={previewAlbumMediaModalRef}
        type={selectedMedia?.kind}
        url={selectedMedia?.url}
        setSelectedUrl={setSelectedMedia}
      />
      <AddMediaToFolderModal modalRef={addMediaToFolderModalRef} onAdd={onAddMediaToFolder} />
      <EditAlbumMediaModal
        modalRef={editAlbumMediaModalRef}
        selectedMedia={selectedMedia}
        setSelectedMedia={setSelectedMedia}
        influencerId={influencerId}
      />
    </>
  );
};

export default UploadedMediaPage;
